/**
 * Utility functions for iOS device detection and compatibility
 */

/**
 * Detects if the current device is running iOS
 * @returns true if the device is iOS (iPhone, iPad, iPod)
 */
export const isIOSDevice = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Detects if the current browser is Safari (including iOS Safari)
 * @returns true if the browser is Safari
 */
export const isSafariBrowser = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /safari/.test(userAgent) && !/chrome/.test(userAgent) && !/chromium/.test(userAgent);
};

/**
 * Detects if the current browser is iOS Safari specifically
 * @returns true if the browser is iOS Safari
 */
export const isIOSSafari = (): boolean => {
    return isIOSDevice() && isSafariBrowser();
};

/**
 * Gets browser and device information for debugging
 * @returns object with browser and device information
 */
export const getBrowserInfo = () => {
    return {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        isIOS: isIOSDevice(),
        isSafari: isSafariBrowser(),
        isIOSSafari: isIOSSafari(),
        maxTouchPoints: navigator.maxTouchPoints,
    };
};

/**
 * Check if we're in a test or staging environment (domain contains "test" or "staging")
 */
const isTestEnvironment = (): boolean => {
    const hostname = window.location.hostname.toLowerCase();
    return hostname.includes('test') || hostname.includes('staging');
};

/**
 * Logs browser information for debugging purposes
 * Only logs in test or staging environments (domain contains "test" or "staging")
 */
export const logBrowserInfo = (): void => {
    if (!isTestEnvironment()) {
        return; // Skip logging in production
    }
    
    const info = getBrowserInfo();
    console.log("Browser Info:", info);
    
    if (info.isIOS) {
        console.log("iOS device detected - applying iOS-specific optimizations");
        
        // Check storage availability on iOS
        try {
            const storageTest = {
                localStorage: typeof Storage !== 'undefined' && typeof localStorage !== 'undefined',
                sessionStorage: typeof Storage !== 'undefined' && typeof sessionStorage !== 'undefined',
                cookies: typeof document !== 'undefined' && typeof document.cookie !== 'undefined'
            };
            console.log("iOS Storage availability:", storageTest);
            
            if (!storageTest.localStorage || !storageTest.sessionStorage) {
                console.warn("iOS storage restrictions detected - this may cause authentication issues");
            }
        } catch (error) {
            console.warn("Error checking iOS storage:", error);
        }
    }
    
    if (info.isIOSSafari) {
        console.log("iOS Safari detected - applying Safari-specific optimizations");
    }
};
